import { useState } from 'react'
import { Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { usePermission } from '@/hooks/usePermission'
import { toApiProblem } from '@/lib/api/client'
import {
  InviteResultsDialog, useEmployeeAccount, useInviteEmployees, type EmployeeInviteResult,
} from '@/features/sysadmin'

/**
 * "Invite to HUMIS" on a profile, for somebody who cannot sign in yet.
 *
 * <p>Hidden once they have an account, invited or active: a second invitation from here would
 * be refused for the email anyway, and re-inviting somebody belongs on the users screen, where
 * the outstanding one is revoked in the same step.
 */
export function InviteToHumisButton({ employeeId }: { employeeId: string }) {
  const canInvite = usePermission('admin:user_manage')
  const account = useEmployeeAccount(employeeId, canInvite)
  const invite = useInviteEmployees()
  const [results, setResults] = useState<EmployeeInviteResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!canInvite || !account.isSuccess || account.data.hasAccount) {
    return results ? <InviteResultsDialog results={results} onClose={() => setResults(null)} /> : null
  }

  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={invite.isPending}
        title={error ?? undefined}
        onClick={() => {
          setError(null)
          invite.mutate({ employeeIds: [employeeId] }, {
            onSuccess: (found) => {
              setResults(found)
              void account.refetch()
            },
            onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'The invitation could not be created.'),
          })
        }}
      >
        <Send className="size-4" aria-hidden="true" />
        Invite to HUMIS
      </Button>
      <InviteResultsDialog results={results} onClose={() => setResults(null)} />
    </>
  )
}
